import React, { CSSProperties, useMemo } from 'react';
import { Box } from '@mui/material';
import { BodyZone, BodyZoneId, ViewType } from '../types';
import { anteriorZones, posteriorZones } from '../utils/bodyZones';
import { designTokens } from '../theme/designTokens';

interface AnatomySelectorProps {
  viewType: ViewType;
  selectedZones: Set<BodyZoneId>;
  onZoneSelected: (zoneId: BodyZoneId) => void;
}

export const AnatomySelector: React.FC<AnatomySelectorProps> = ({
  viewType,
  selectedZones,
  onZoneSelected,
}) => {
  const currentZones = useMemo(
    () => (viewType === ViewType.front ? anteriorZones : posteriorZones),
    [viewType]
  );

  const aspectRatio = viewType === ViewType.front ? 176 / 429 : 177 / 431;
  const baseAsset =
    viewType === ViewType.front
      ? '/assets/scan_locations/front/base.svg'
      : '/assets/scan_locations/back/base.svg';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          aspectRatio: aspectRatio.toString(),
          maxHeight: '55vh',
        }}
      >
        {/* Base anatomy outline */}
        <Box
          component="img"
          src={baseAsset}
          alt={`${viewType} view anatomy`}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />

        {/* Body zones */}
        {currentZones.map((zone) => (
          <ZoneOverlay
            key={zone.id}
            zone={zone}
            isSelected={selectedZones.has(zone.id)}
            onSelect={() => onZoneSelected(zone.id)}
          />
        ))}
      </Box>
    </Box>
  );
};

interface ZoneOverlayProps {
  zone: BodyZone;
  isSelected: boolean;
  onSelect: () => void;
}

const ZoneOverlay: React.FC<ZoneOverlayProps> = ({ zone, isSelected, onSelect }) => {
  const containerStyle: CSSProperties = {
    position: 'absolute',
    top: `${zone.top * 100}%`,
    left: `${zone.left * 100}%`,
    height: `${zone.height * 100}%`,
    cursor: 'pointer',
    transition: 'opacity 0.2s ease-in-out',
  };

  const overlayStyle: CSSProperties = {
    height: '100%',
    width: 'auto',
    objectFit: 'contain',
    filter: isSelected
      ? `opacity(0.5)`
      : 'opacity(0.2)',
    transition: 'filter 0.2s ease-in-out',
  };

  return (
    <Box
      style={containerStyle}
      onClick={onSelect}
      onMouseEnter={(e) => {
        if (!isSelected) {
          const img = e.currentTarget.querySelector('img');
          if (img) {
            (img as HTMLImageElement).style.filter = 'opacity(0.15)';
          }
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          const img = e.currentTarget.querySelector('img');
          if (img) {
            (img as HTMLImageElement).style.filter = 'opacity(0.08)';
          }
        }
      }}
    >
      <img
        src={zone.svgAssetPath}
        alt={zone.id}
        style={overlayStyle}
        draggable={false}
      />
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: '100%',
            bgcolor: designTokens.colors.brand.blue.primary,
            opacity: 0.33,
            pointerEvents: 'none',
            WebkitMaskImage: `url(${zone.svgAssetPath})`,
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            WebkitMaskSize: 'contain',
            maskImage: `url(${zone.svgAssetPath})`,
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
            maskSize: 'contain',
          }}
        />
      )}
    </Box>
  );
};
